import { Link, useNavigate, useLocation } from 'react-router-dom';
import { CircleHelp, ClipboardList, KeyRound, LogOut, Menu, MessageCircle, Rocket, Search, User, Users, Webhook } from 'lucide-react';
import { Button } from './ui/button';
import { UserAvatar } from './UserAvatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { Tooltip, TooltipContent, TooltipTrigger } from './ui/tooltip';
import { clearAuthSession } from '../lib/api';
import { isThemeMode, setThemeMode } from '../lib/theme';
import { useOverlays } from './work/overlay-context';
import { useSession } from './session-context';
import { useThemeMode } from './use-theme';
import ProjectSwitcher from './work/ProjectSwitcher';
import NotificationsBell from './work/NotificationsBell';
import ThemeToggle from './ThemeToggle';

type Props = {
  onOpenMobileNav?: () => void;
};

export default function AppHeader({ onOpenMobileNav }: Props) {
  const { ready, email, avatarUrl, isAdmin, isOrgAdmin, viewAs } = useSession();
  const navigate = useNavigate();
  const location = useLocation();
  const { openPowerK, openShortcuts } = useOverlays();
  const themeMode = useThemeMode();

  async function onLogout() {
    try {
      await fetch('/auth/logout', { method: 'POST' });
    } finally {
      clearAuthSession();
      navigate('/');
    }
  }

  return (
    <header className="sticky top-0 z-40 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80">
      <div className="flex h-12 items-center gap-3 px-4 md:px-6">
        {onOpenMobileNav ? (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="md:hidden"
            onClick={onOpenMobileNav}
          >
            <Menu className="h-4 w-4" />
            <span className="sr-only">Open navigation</span>
          </Button>
        ) : null}
        {ready && email ? <ProjectSwitcher /> : null}
        <div className="ml-auto flex items-center gap-2">
          {!email ? <ThemeToggle /> : null}
          {!ready ? (
            <span className="h-8 w-8" aria-hidden />
          ) : email ? (
            <>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className="h-8 gap-2 rounded-full px-3 font-normal text-muted-foreground shadow-none"
                    onClick={openPowerK}
                    aria-label="Search and create"
                  >
                    <Search className="h-3.5 w-3.5" aria-hidden />
                    <span className="hidden sm:inline">Search...</span>
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Search and create ⌘K</TooltipContent>
              </Tooltip>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    type="button"
                    size="icon"
                    variant="ghost"
                    onClick={openShortcuts}
                    aria-label="Keyboard shortcuts"
                  >
                    <CircleHelp className="h-4 w-4" aria-hidden />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Keyboard shortcuts ⌘/</TooltipContent>
              </Tooltip>
              <NotificationsBell />
              <ThemeToggle />
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" className="rounded-full">
                    <UserAvatar name={email} src={avatarUrl} size={32} />
                    <span className="sr-only">Account menu</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel className="font-normal">
                    <p className="text-sm font-medium">{viewAs ? `Viewing as ${viewAs.name}` : 'Signed in'}</p>
                    <p className="truncate text-xs text-muted-foreground">{email}</p>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem asChild>
                    <Link to="/settings/profile">
                      <User />
                      Profile
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/settings/members">
                      <Users />
                      Members
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/settings/tokens">
                      <KeyRound />
                      Tokens
                    </Link>
                  </DropdownMenuItem>
                  {isOrgAdmin ? (
                    <DropdownMenuItem asChild>
                      <Link to="/settings/service-requests">
                        <ClipboardList />
                        Service Requests
                      </Link>
                    </DropdownMenuItem>
                  ) : null}
                  {isAdmin ? (
                    <DropdownMenuItem asChild>
                      <Link to="/settings/business-requirements">
                        <Rocket />
                        Business Requirements
                      </Link>
                    </DropdownMenuItem>
                  ) : null}
                  {isOrgAdmin ? (
                    <DropdownMenuItem asChild>
                      <Link to="/settings/webhooks">
                        <Webhook />
                        Webhooks
                      </Link>
                    </DropdownMenuItem>
                  ) : null}
                  {isOrgAdmin ? (
                    <DropdownMenuItem asChild>
                      <Link to="/settings/whatsapp">
                        <MessageCircle />
                        WhatsApp
                      </Link>
                    </DropdownMenuItem>
                  ) : null}
                  <DropdownMenuSeparator />
                  <DropdownMenuLabel className="text-xs font-medium text-muted-foreground">
                    Theme
                  </DropdownMenuLabel>
                  <DropdownMenuRadioGroup
                    value={themeMode}
                    onValueChange={(value) => {
                      if (!isThemeMode(value)) return;
                      setThemeMode(value);
                    }}
                  >
                    <DropdownMenuRadioItem value="system">System</DropdownMenuRadioItem>
                    <DropdownMenuRadioItem value="light">Light</DropdownMenuRadioItem>
                    <DropdownMenuRadioItem value="dark">Dark</DropdownMenuRadioItem>
                  </DropdownMenuRadioGroup>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onSelect={() => void onLogout()}>
                    <LogOut />
                    Log out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </>
          ) : location.pathname === '/login' ? null : (
            <Button asChild size="sm">
              <Link to="/login">Login</Link>
            </Button>
          )}
        </div>
      </div>
    </header>
  );
}
